import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Img, { ImgProps } from "@insite/mobius/Img";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEmblaCarousel } from "embla-carousel/react";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";

const enum fields {
    heading = "heading",
    stories = "stories",
    icon = "icon",
    title = "title",
    description = "description",
    alignment = "alignment",
    label = "label",
    image = "image",
    customerName = "customerName",
    customerCompany = "customerCompany",
    buttonLink = "buttonLink",
    imageFirst = "imageFirst",
    textContainerWidth = "textContainerWidth",
}

type Story = {
    fields: {
        [fields.icon]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.alignment]: string;
        [fields.label]: string;
        [fields.image]: string;
        [fields.customerName]: string;
        [fields.customerCompany]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.imageFirst]: boolean;
        [fields.textContainerWidth]: number;
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.stories]: Story[];
        [fields.heading]: string;
    };
}

type Props = OwnProps & HasShellContext;

export interface StoriesStyles {
    wrapper?: InjectableCss;
    headerContainer?: InjectableCss;
    titleText?: TypographyPresentationProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowButton?: ButtonPresentationProps;
    mainContainer?: GridContainerProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    mainGridContainer: GridContainerProps;
    titleLabel?: TypographyPresentationProps;
    descriptionText?: TypographyPresentationProps;
    buttonContainer?: GridItemProps;
    ctaButton?: ButtonPresentationProps;
    detailsContainer?: GridItemProps;
    imageContainer?: GridItemProps;
    image?: ImgProps;
    headingWrapper?: InjectableCss;
    customerNameText?: TypographyPresentationProps;
    customerCompanyText?: TypographyPresentationProps;
    icon?: ImgProps;
}

export const customerStoriesStyles: StoriesStyles = {
    wrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 24px 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    headerContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    titleText: {
        variant: "h3",
        css: css`
            text-align: center;
            margin-bottom: 0;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        buttonType: "outline",
        color: "text.main",
        css: css`
            border: 2px solid ${getColor("common.border")};
            margin-right: 12px;
            padding: 12px;
            &:hover,
            &:active,
            &:focus {
                background-color: ${getColor("common.background")} !important;
                border: 2px solid ${getColor("common.border")} !important;
            }
        `,
    },

    nextArrowButton: {
        variant: "secondary",
        buttonType: "outline",
        color: "text.main",
        css: css`
            border: 2px solid ${getColor("common.border")};
            padding: 12px;
            &:hover,
            &:active,
            &:focus {
                background-color: ${getColor("common.background")} !important;
                border: 2px solid ${getColor("common.border")} !important;
            }
        `,
    },
    mainContainer: {
        gap: 0,
        offsetCss: css`
            width: 100% !important;
        `,
    },
    carouselGridItem: {
        width: 12,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },

    mainGridContainer: {},

    icon: {
        css: css`
            height: 24px;
            width: 24px;
        `,
    },
    titleLabel: {
        variant: "p",
        weight: 600,
        css: css`
            margin-bottom: 0;
        `,
    },
    descriptionText: {
        css: css`
            margin-bottom: 40px;
            > p {
                margin-bottom: 0;
            }
        `,
    },
    // buttonContainer: {
    //     css: css`
    //         padding-top: 40px;
    //         justify-content: center;
    //     `,
    // },
    // ctaButton: {
    //     variant: "primary",
    //     css: css`
    //         ${({ theme }: { theme: BaseTheme }) =>
    //             breakpointMediaQueries(
    //                 theme,
    //                 [
    //                     css`
    //                         width: 100%;
    //                     `,
    //                     css``,
    //                     css`
    //                         width: 160px;
    //                     `,
    //                 ],
    //                 "min",
    //             )}
    //     `,
    // },
    detailsContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 24px;
                        `,
                        null,
                        css`
                            padding: 56px 80px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    image: {
        css: css`
            width: 100%;
            max-width: 100%;
            height: auto;
        `,
    },
    headingWrapper: {
        css: css`
            margin-bottom: 40px;
            display: flex;
            gap: 1rem;
        `,
    },
    customerNameText: {
        variant: "p",
        weight: 600,
        css: css`
            margin-bottom: 4px;
        `,
    },
    customerCompanyText: {},
    imageContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            display: flex;
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

const OrderWrapper = styled.div<{ order: number, imageWrapper?: boolean }>`
    order: ${props => props.order};
    display: ${props => props.imageWrapper ? "flex" : "block"};
    align-items: ${props => props.imageWrapper ? "center" : ""};
    justify-content: ${props => props.imageWrapper ? "center" : ""};
`;

export const CustomerStories = ({ fields, shellContext }: Props) => {
    const { stories } = fields;
    const styles = customerStoriesStyles;
    const [emblaRef, embla] = useEmblaCarousel();
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const setCanScroll = () => {
        // if (shellContext.isInShell) {
        //     return;
        // }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const [slidesToScroll] = useState(1);

    useEffect(() => {
        if (!embla) {
            return;
        }
        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);
    }, [embla]);

    useEffect(() => {
        if (!embla || !stories || stories.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            draggable: isTouchDevice(),
            loop: false,
        });
        setCanScroll();
    }, [embla, slidesToScroll, stories]);

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.headerContainer}>
                <Typography {...styles.titleText}>{fields.heading}</Typography>
                <StyledWrapper>
                    {/* Pre Button */}
                    {stories.length > slidesToScroll && (
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                            data-test-selector="prevBtn"
                        >
                            <ButtonIcon src={ChevronLeft} />
                        </Button>
                    )}
                    {/* Next button */}
                    {stories.length > slidesToScroll && (
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            disabled={!canScrollNext}
                            data-test-selector="nextBtn"
                        >
                            <ButtonIcon src={ChevronRight} />
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <GridContainer {...styles.mainContainer} id="carousel-main-container">
                <GridItem {...styles.carouselGridItem}>
                    <StyledWrapper {...styles.carouselWrapper}>
                        <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                            <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                {fields?.stories?.map((story, ind) => (
                                    <CustomerStory fields={story.fields} key={ind}></CustomerStory>
                                ))}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

export const CustomerStory = ({ fields }: Story) => {
    const styles = customerStoriesStyles;
    const { url } = useGetLink(fields.buttonLink);
    const { isInShell } = useShellContext();
    const history = useHistory();
    const isImageFirst = fields.imageFirst;
    const { textContainerWidth } = fields;

    const imageOrder = isImageFirst ? -1 : 1;
    const textOrder = isImageFirst ? 1 : -1;

    const handleBtnClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const mainGridContainerStyles: GridContainerProps = {
        css: css`
            width: 100%;
            display: grid;
            border: 1px solid ${getColor("common.border")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin: 24px 0 0 0;
                            grid-template-columns: repeat(1, 1fr);
                        `,
                        null,
                        css`
                            grid-template-columns: ${textContainerWidth}% ${100 - textContainerWidth}%;
                        `,
                    ],
                    "min",
                )}
        `,
    };

    return (
        <StyledWrapper {...styles.carouselSlide} style={{ width: `100%` }}>
            <StyledWrapper {...mainGridContainerStyles}>
                <OrderWrapper order={textOrder}>
                    <StyledWrapper {...styles.detailsContainer}>
                        {/* <StyledWrapper {...styles.headingWrapper}>
                        <Img src={fields.icon} />
                        <Typography {...styles.titleLabel}>{fields.title}</Typography>
                    </StyledWrapper> */}

                        <Typography {...styles.descriptionText}>{parse(fields.description, parserOptions)}</Typography>
                        <Typography {...styles.customerNameText}>
                            {parse(fields.customerName, parserOptions)}
                        </Typography>
                        <Typography {...styles.customerCompanyText}>
                            {parse(fields.customerCompany, parserOptions)}
                        </Typography>
                        {/* <StyledWrapper {...styles.buttonContainer}>
                        <Button {...styles.ctaButton} onClick={() => handleBtnClick(url)}>
                            {fields.label}
                        </Button>
                    </StyledWrapper> */}
                    </StyledWrapper>
                </OrderWrapper>

                <OrderWrapper order={imageOrder} imageWrapper={true}>
                    <StyledWrapper {...styles.imageContainer}>
                        <Img src={fields.image} {...styles.image} />
                    </StyledWrapper>
                </OrderWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withIsInShell(CustomerStories),
    definition: {
        group: "Basic",
        displayName: "Stories",
        fieldDefinitions: [
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "TextField",
                defaultValue: "Customer stories",
                fieldType: "Contextual",
            },
            {
                name: fields.stories,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    // {
                    //     name: fields.icon,
                    //     displayName: "Icon",
                    //     editorTemplate: "ImagePickerField",
                    //     defaultValue: "",
                    // },
                    {
                        name: fields.title,
                        displayName: "Display Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.description,
                        displayName: "Description",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.customerName,
                        displayName: "Customer Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.customerCompany,
                        displayName: "Customer Company",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    // {
                    //     name: fields.buttonLink,
                    //     displayName: "Button Link",
                    //     editorTemplate: "LinkField",
                    //     defaultValue: { type: "Page", value: "" },
                    // },
                    // {
                    //     name: fields.label,
                    //     displayName: "Button Label",
                    //     editorTemplate: "TextField",
                    //     defaultValue: "",
                    // },
                    {
                        name: fields.image,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: fields.imageFirst,
                        displayName: "Show Image First",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: fields.textContainerWidth,
                        displayName: "Text container width(%)",
                        editorTemplate: "IntegerField",
                        defaultValue: 50,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
